import { useMemo } from 'react';
import { SafetyOutlined } from '@ant-design/icons';

import { ChonQuyenChiaDoi, LuoiChonQuyen } from '../../../components/shared';
import { Button, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { NHAN_NHOM_NEN_TANG, NHAN_QUYEN_NEN_TANG } from '../constants';
import { MA_QUYEN_NEN_TANG, gomTheoModule, moTaQuyen } from '../nghiep-vu/vai-tro';
import type { Permission } from '../types';

export interface ChonQuyenProps {
  danhMuc: Permission[] | undefined;
  dangTai: boolean;
  loi: unknown;
  onThuLai: () => void;
  daChon: ReadonlySet<string>;
  onDoi: (moi: Set<string>) => void;
  /** Lỗi `422` theo mã quyền — hiện ngay dưới đúng ô. */
  loiTheoQuyen?: Record<string, string>;
  voHieu?: boolean;
  /** Xếp nhóm thành nhiều cột — cho hộp thoại rộng (`LuoiChonQuyen`). */
  luoi?: boolean;
  /**
   * Bố cục chia đôi (danh sách module bên trái, quyền bên phải) cho tổ chức nhiều module. Truyền tập quyền đã lưu (khi
   * sửa) để báo thay đổi chưa lưu; tạo mới thì để trống. Cần khung cao: cha phải là cột flex có chiều cao cố định.
   */
  chiaDoi?: { daLuu?: ReadonlySet<string> };
}

/**
 * Chọn quyền gom theo module (service SDK): lo tải, lỗi và rỗng của danh mục,
 * phần lưới (ô tìm, chọn cả nhóm, từng quyền) là `LuoiChonQuyen` dùng chung.
 *
 * Dùng chung cho hộp tạo vai trò và ngăn phân quyền. Đủ bốn trạng thái của
 * danh mục; danh mục rỗng là hợp lệ — tổ chức chưa cài module nào — nên có
 * câu riêng, không phải lỗi.
 *
 * `module_name` là dữ liệu của server, không dịch. Mô tả quyền do module khai
 * bằng từng ngôn ngữ (`description_i18n`, ADR-0111) nên chọn theo giao diện, rơi
 * về `description` khi thiếu. Nhóm quyền nền tảng `core` (ADR-0104) do nền tảng
 * đặt nên dịch bằng catalog của giao diện.
 */
export function ChonQuyen({
  danhMuc,
  dangTai,
  loi,
  onThuLai,
  daChon,
  onDoi,
  loiTheoQuyen = {},
  voHieu,
  luoi,
  chiaDoi,
}: ChonQuyenProps) {
  const { t, ngonNgu } = useT();
  const dichLoi = useLoi();
  const nhom = useMemo(() => gomTheoModule(danhMuc ?? [], ngonNgu), [danhMuc, ngonNgu]);

  if (dangTai) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label={t('vaiTroLop2.dangTaiQuyen')}>
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2 rounded-lg border border-default p-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (loi && !danhMuc) {
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default px-4 py-3"
      >
        <span className="min-w-0 text-sm text-danger-700">
          {t('vaiTroLop2.loiDanhMucQuyen', { loi: dichLoi(loi) })}
        </span>
        <Button variant="outline" size="sm" onClick={onThuLai}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }

  if (nhom.length === 0) {
    return (
      <Empty
        className="rounded-lg border border-dashed border-default py-8"
        icon={SafetyOutlined}
        title={t('vaiTroLop2.danhMucRong')}
        description={t('vaiTroLop2.danhMucRongMo')}
      />
    );
  }

  const nhomChon = nhom.map((n) => {
    const nenTang = n.module_code === MA_QUYEN_NEN_TANG;
    return {
      khoa: n.module_code,
      nhan: nenTang ? t(NHAN_NHOM_NEN_TANG) : n.module_name,
      quyen: n.quyen.map((q) => {
        const khoa = nenTang ? NHAN_QUYEN_NEN_TANG[q.code] : undefined;
        // Manifest không mô tả quyền thì hiện mã, để dòng không trống
        return { code: q.code, nhan: khoa ? t(khoa) : moTaQuyen(q, ngonNgu) };
      }),
    };
  });

  if (chiaDoi) {
    return (
      <ChonQuyenChiaDoi
        nhom={nhomChon}
        daChon={daChon}
        daLuu={chiaDoi.daLuu}
        onDoi={onDoi}
        loiTheoQuyen={loiTheoQuyen}
        voHieu={voHieu}
      />
    );
  }

  return (
    <LuoiChonQuyen
      nhom={nhomChon}
      daChon={daChon}
      onDoi={onDoi}
      loiTheoQuyen={loiTheoQuyen}
      voHieu={voHieu}
      luoi={luoi}
    />
  );
}
